import { clsx, type ClassValue } from 'clsx'
import { twMerge } from 'tailwind-merge'

export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs))
}

/** Deterministic non-cryptographic hash, used for placeholder covers and avatars. */
export function hashString(value: string): number {
  let h = 2166136261
  for (let i = 0; i < value.length; i++) {
    h ^= value.charCodeAt(i)
    h = Math.imul(h, 16777619)
  }
  return h >>> 0
}

/**
 * A stable hue per collection slug (§11.2). Derived rather than hand-picked so
 * a new collection needs no design work.
 */
export function collectionHue(collection: string): number {
  return hashString(collection) % 360
}

/**
 * File sizes carry a localised unit: the Russian and Uzbek interfaces say МБ
 * and ГБ, not MB and GB. The caller passes the already-translated unit labels
 * so this stays free of an i18n import.
 */
export function formatBytes(
  bytes: number,
  locale: string,
  units: { mb: string; gb: string } = { mb: 'MB', gb: 'GB' },
): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return ''
  const mb = bytes / (1024 * 1024)
  if (mb >= 1024) {
    const gb = new Intl.NumberFormat(locale, { maximumFractionDigits: 2 }).format(mb / 1024)
    return `${gb}\u00a0${units.gb}`
  }
  const value = new Intl.NumberFormat(locale, { maximumFractionDigits: mb < 10 ? 1 : 0 }).format(mb)
  return `${value}\u00a0${units.mb}`
}

export function formatNumber(value: number, locale: string): string {
  return new Intl.NumberFormat(locale).format(value)
}

export function formatDate(iso: string | null | undefined, locale: string): string {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  return new Intl.DateTimeFormat(locale, { dateStyle: 'medium' }).format(d)
}

export function formatDateTime(iso: string | null | undefined, locale: string): string {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  return new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short' }).format(d)
}

/** `sum / count`, or null for an empty pool — never 0.0 (§12.7 item 9). */
export function average(sum: number, count: number): number | null {
  return count > 0 ? sum / count : null
}

export function initials(displayName: string): string {
  const parts = displayName.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return '?'
  if (parts.length === 1) return (parts[0] ?? '').slice(0, 2).toUpperCase()
  return `${(parts[0] ?? '')[0] ?? ''}${(parts[parts.length - 1] ?? '')[0] ?? ''}`.toUpperCase()
}

export function truncate(value: string, max: number): string {
  return value.length <= max ? value : `${value.slice(0, max - 1)}…`
}

/** Locale tags for Intl (§FR-7.7). */
export const INTL_LOCALES: Record<string, string> = {
  en: 'en-US',
  ru: 'ru-RU',
  uz: 'uz-Latn-UZ',
}

export function intlLocale(language: string): string {
  return INTL_LOCALES[language] ?? 'en-US'
}

export function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value))
}

export function unique<T>(values: T[]): T[] {
  return Array.from(new Set(values))
}

/** Small typed helper for building query strings without `undefined` entries. */
export function toQuery(params: Record<string, string | number | boolean | undefined | null>): string {
  const sp = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue
    sp.set(key, String(value))
  }
  const s = sp.toString()
  return s ? `?${s}` : ''
}
